/**
 * 問題の並びのサイドバー (ProblemSidebar) に出す短い題。全角 12 字以内 (半角は 0.5 字) で、問題の中身が分かる名前にする。
 * 代表の見本 (2026-10-30) の「重複チェック」「アナグラム判定」「二分探索」の長さ。
 * 原稿 (content) の題は検索に出す長い文なので変えない。ここに無い問題は、サイドバーも原稿の題を出す。
 * 全部の slug が揃っていることと字数は problem-short-titles.test.ts が見る。
 */

export const CODING_SHORT_TITLES: Readonly<Record<string, string>> = {
  "all-subsets": "部分集合の全列挙",
  "balanced-tree": "釣り合った二分木",
  "binary-search": "二分探索",
  "build-tree-from-orders": "前順と中順から木",
  "circle-robber": "輪になった隣り合わない和",
  "climb-stairs": "階段の上り方",
  "coin-change": "最少枚数のおつり",
  "coin-ways": "硬貨の組み合わせ数",
  "combination-sum": "足して目標になる組",
  "common-subsequence": "最長共通部分列",
  "compress-runs": "ランレングス圧縮",
  "container-most-water": "水をためる板",
  "contains-duplicate": "重複チェック",
  "count-provinces": "つながった町の数",
  "course-order": "講座を受ける順",
  "course-schedule": "全講座を受けられるか",
  "delete-bst": "二分探索木の削除",
  "design-twitter": "SNS のタイムライン",
  "edit-distance": "編集距離",
  "equal-partition": "同じ重さの 2 組",
  "eval-rpn": "逆ポーランド記法",
  "find-peak": "山の頂上を探す",
  "first-last-position": "同じ値の両端",
  "first-unique-char": "最初の重複しない文字",
  "flatten-list": "入れ子の配列を平らに",
  "four-sum": "和が目標の 4 つ組",
  "grid-shortest-path": "グリッドの最短経路",
  "group-anagrams": "アナグラムの仲間分け",
  "implement-trie": "トライ木の実装",
  "insert-bst": "二分探索木に追加",
  "insert-position": "挿入位置",
  "invert-tree": "二分木の左右反転",
  "is-palindrome": "回文判定",
  "koko-eating-speed": "間に合う最小の速さ",
  "kth-largest": "k 番目に大きい値",
  "kth-smallest-bst": "木の k 番目に小さい値",
  "lca-bst": "探索木の共通の祖先",
  "lca-tree": "二分木の共通の祖先",
  "linked-list-cycle": "連結リストの輪",
  "longest-increasing-subsequence": "最長増加部分列",
  "longest-palindrome-substring": "最長の回文部分文字列",
  "longest-unique-substring": "重複なしの最長部分",
  "lru-cache": "LRU キャッシュ",
  "max-average-window": "平均が最大の窓",
  "max-consecutive-ones": "続く 1 の最長",
  "max-subarray-sum": "最大部分配列和",
  "median-finder": "流れる数の中央値",
  "median-two-sorted": "2 配列の中央値",
  "merge-intervals": "区間のまとめ",
  "merge-sorted-lists": "並んだリストの結合",
  "min-stack": "最小値つきスタック",
  "move-zeros-to-end": "0 を末尾へ",
  "no-adjacent-max": "隣り合わない最大和",
  "number-of-islands": "島の数",
  "pacific-atlantic": "両方の海へ流れる所",
  "pair-with-target": "Two Sum",
  "palindrome-number": "回文の整数",
  "permutations": "並べ方の全列挙",
  "product-except-self": "自分以外の積",
  "randomized-set": "無作為に取れる集合",
  "reverse-linked-list": "連結リストの逆順",
  "reverse-words": "単語の逆順",
  "root-to-leaf-sum": "根から葉までの合計",
  "rotated-array-search": "回された並びの探索",
  "same-letters": "アナグラム判定",
  "same-tree": "同じ二分木か",
  "sort-colors": "3 色の並べ替え",
  "sorted-array-to-bst": "並びから探索木",
  "sorted-pair-sum": "整列済みの Two Sum",
  "stock-best-time": "株の最大利益",
  "target-sum": "符号の付け方の数",
  "three-sum-closest": "目標に近い 3 つの和",
  "three-sum": "和が 0 の 3 つ組",
  "time-based-store": "時刻つきの履歴",
  "top-k-frequent": "多い順に k 個",
  "tree-depth": "二分木の深さ",
  "tree-diameter": "二分木の直径",
  "tree-levels": "段ごとの読み出し",
  "valid-brackets": "括弧の対応",
  "validate-bst": "二分探索木の検証",
  "word-break": "単語への区切り",
  "word-dictionary": "伏せ字の言葉探し",
  "word-ladder": "言葉の梯子",
};

export const INTERVIEW_SHORT_TITLES: Readonly<Record<string, string>> = {
  "adopting-new-tech": "新技術の採用判断",
  "ai-agents-tools": "AI エージェントの運用",
  "ai-tools-team-adoption": "AI ツールのチーム導入",
  "api-latency-investigation": "API が遅いときの調査",
  "api-versioning": "API のバージョン管理",
  "array-map-filter-reduce": "map と filter と reduce",
  "arrow-function-this": "アロー関数と this",
  "authn-vs-authz": "認証と認可",
  "authorization-design": "権限の仕組みの設計",
  "autoscaling": "オートスケールの注意",
  "browser-cache": "ブラウザのキャッシュ",
  "build-vs-buy": "作るか買うか",
  "cache-strategy": "キャッシュの置き方",
  "cascading-failure": "障害の連鎖を防ぐ",
  "ci-cd": "CI と CD の組み方",
  "closure": "クロージャ",
  "cloud-cost": "クラウド費用の見直し",
  "code-review-culture": "レビューの回し方",
  "concurrent-update": "同時更新の防ぎ方",
  "cookie-session": "Cookie とセッション",
  "cors-error": "CORS エラー",
  "cross-service-consistency": "サービス間の整合性",
  "csr-ssr-ssg": "CSR と SSR と SSG",
  "csrf": "CSRF",
  "data-backfill": "大量データの書き換え",
  "db-read-scaling": "DB の読み込み対策",
  "deadline-negotiation": "無理な納期の交渉",
  "deadlock": "デッドロック",
  "design-docs": "設計書の書き方",
  "dns-how": "DNS のしくみ",
  "embeddings-vector-search": "埋め込みとベクトル検索",
  "environments": "環境の分け方",
  "error-handling": "エラー処理",
  "estimate-overrun": "見積もりが外れたら",
  "explain-portfolio": "作品の説明",
  "explain-what-you-learned": "学んだことの説明",
  "fde-adoption": "現場に定着させる",
  "fde-cannot-answer-now": "その場で答えられないとき",
  "fde-connect-existing-systems": "既存システムとの接続",
  "fde-custom-vs-product": "作り込みと共通機能",
  "fde-customer-access-secrets": "顧客環境の権限と秘密",
  "fde-eval-set": "AI の評価セット",
  "fde-exec-vs-field": "役員と現場の食い違い",
  "fde-expectation-reset": "期待のすり合わせ直し",
  "fde-explain-to-non-engineers": "非エンジニアへの説明",
  "fde-feedback-to-product": "製品へのフィードバック",
  "fde-find-ai-failures": "AI の失敗の見つけ方",
  "fde-first-week-onsite": "先方に入る最初の週",
  "fde-incident-onsite": "現場での障害対応",
  "fde-prototype-to-production": "試作から本番へ",
  "fde-real-problem": "本当の課題の見つけ方",
  "fde-success-metrics": "成功の物差し",
  "fde-team-knowledge": "チームでの知見の共有",
  "fde-two-week-scope": "最初の 2 週間の範囲",
  "fde-weekly-report": "週次の報告",
  "fde-what-is": "FDE とは",
  "feature-flags-release": "機能フラグで段階公開",
  "frontend-state": "フロントの状態管理",
  "get-vs-post": "GET と POST",
  "git-add-commit-push": "add と commit と push",
  "git-branch-flow": "ブランチの使い方",
  "git-conflict": "コンフリクトの解消",
  "git-merge-rebase": "merge と rebase",
  "git-revert-reset": "revert と reset",
  "hallucination": "ハルシネーション",
  "hardest-problem": "いちばん苦労したこと",
  "hiring-interviewer": "採用面接の見方",
  "how-you-learn-new-tech": "新技術の学び方",
  "how-you-use-ai": "AI の使い方",
  "http-status-codes": "HTTP ステータスコード",
  "https-what-it-protects": "HTTPS が守るもの",
  "idempotency-key": "二重送信と冪等性",
  "incident-first-response": "障害の最初の 30 分",
  "index-tradeoff": "インデックスの得失",
  "inner-left-join": "INNER と LEFT JOIN",
  "ip-and-port": "IP とポート",
  "isolation-level": "分離レベル",
  "knowledge-silo": "属人化の解消",
  "large-file-upload": "大きなファイルの送信",
  "layer-separation": "層を分ける理由",
  "legacy-refactoring": "古いコードの改修",
  "let-const-var": "let と const と var",
  "llm-cost-latency": "LLM の費用と速さ",
  "llm-evaluation": "LLM の品質評価",
  "llm-privacy": "LLM とプライバシー",
  "llm-tokens-context": "トークンと文脈の長さ",
  "load-balancer-cdn": "ロードバランサと CDN",
  "load-testing": "負荷試験",
  "long-running-jobs": "長い処理の動かし方",
  "mentoring": "後輩の育て方",
  "mobile-app-choice": "モバイルの技術選び",
  "monitoring-alerts": "監視と警報",
  "monolith-or-microservices": "モノリスか分割か",
  "n-plus-one": "N+1 問題",
  "network-layers": "ネットワークの階層",
  "non-functional-requirements": "非機能要件",
  "normalization": "正規化",
  "normalize-or-denormalize": "正規化か非正規化か",
  "null-undefined": "null と undefined",
  "oauth-pkce": "OAuth と PKCE",
  "observability": "ログと指標と追跡",
  "on-call": "オンコールの組み方",
  "onboarding": "新人の立ち上げ",
  "pagination": "ページ送り",
  "passkeys": "パスキー",
  "password-storage": "パスワードの保存",
  "personal-data-law": "個人情報と法令",
  "postmortem": "ポストモーテム",
  "primary-foreign-key": "主キーと外部キー",
  "prioritization": "優先順位の付け方",
  "project-estimation": "案件の見積もり",
  "promise-async-await": "Promise と async await",
  "prompt-injection": "プロンプト注入",
  "pull-request-review": "プルリクエストの作法",
  "push-notifications": "プッシュ通知",
  "rag-accuracy": "RAG の精度が低い",
  "rag-vs-finetuning": "RAG と微調整",
  "rate-limiting": "回数制限と不正対策",
  "react-props-drilling-context": "props と Context",
  "react-rerender": "再レンダリングの削減",
  "react-useeffect": "useEffect",
  "react-useref": "useRef",
  "readable-code": "読みやすいコード",
  "realtime-websocket": "リアルタイム更新",
  "requirements-hearing": "要件のヒアリング",
  "rest-api": "REST API",
  "rest-vs-graphql": "REST と GraphQL",
  "scope-change": "仕様変更の扱い",
  "secret-rotation": "鍵の管理と交換",
  "secrets-in-code": "鍵をコードに書かない",
  "secure-development-process": "開発に組む安全確認",
  "security-incident-response": "情報漏えいの対応",
  "serverless-vs-container": "サーバーレスとコンテナ",
  "session-vs-jwt": "セッションと JWT",
  "single-responsibility": "単一責任",
  "slo-error-budget": "SLO とエラーバジェット",
  "slow-query-investigation": "遅い SQL の調査",
  "sql-injection": "SQL インジェクション",
  "storage-choice": "保存先の使い分け",
  "strict-equality": "== と ===",
  "struggling-member": "伸び悩むメンバー",
  "supply-chain": "サプライチェーン攻撃",
  "sync-vs-async": "同期と非同期",
  "system-replacement": "システムの置き換え",
  "tcp-vs-udp": "TCP と UDP",
  "team-delivery-metrics": "開発の速さと質の測り方",
  "team-test-strategy": "テストの方針",
  "tech-debt": "技術的負債",
  "tech-lead-role": "テックリードの責任",
  "technical-disagreement": "設計の意見の割れ",
  "tight-budget-tech-choice": "厳しい予算での技術選び",
  "timeout-retry": "タイムアウトと再試行",
  "transaction-acid": "トランザクション",
  "typescript-why": "TypeScript の利点",
  "unit-vs-integration-test": "単体と結合テスト",
  "url-to-screen": "URL から画面まで",
  "web-vitals": "表示が遅いときの測定",
  "where-having": "WHERE と HAVING",
  "why-this-tech": "その技術を選んだ理由",
  "why-write-tests": "テストを書く理由",
  "xss": "XSS",
  "zero-downtime-migration": "止めずにスキーマ変更",
  "zero-trust": "ゼロトラスト",
};

export const SHORT_TITLE_MAX_WIDTH = 12;

/** 字数は全角を 1、半角を 0.5 と数える */
export function titleWidth(text: string) {
  return [...text].reduce((sum, ch) => sum + (ch.charCodeAt(0) < 128 ? 0.5 : 1), 0);
}

export function codingShortTitle(slug: string, title: string) {
  return CODING_SHORT_TITLES[slug] ?? title;
}

export function interviewShortTitle(slug: string, title: string) {
  return INTERVIEW_SHORT_TITLES[slug] ?? title;
}
